import {
  Button,
  Container,
  Paper,
  Stack,
  TextInput,
  Title,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { fetchApi } from "@/lib/api";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { useEffect } from "react";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";
import { useUser } from "@clerk/clerk-react";

export const Route = createFileRoute("/onboarding")({
  component: OnboardingPage,
});

interface OnboardingFormData {
  name: string;
  email: string;
}

interface OnboardingResponse {
  user: {
    id: string;
    name: string;
    email: string;
  };
}

function OnboardingPage() {
  const navigate = useNavigate();
  const auth = useAuthConditional();
  const { data: onboardingStatus, isLoading: isLoadingStatus } =
    useOnboardingStatus();

  // Get user info from Clerk if available
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const clerkUser = CLERK_AVAILABLE ? useUser() : null;
  const userEmail = clerkUser?.user?.primaryEmailAddress?.emailAddress || "";
  const userFullName = clerkUser?.user?.fullName || "";

  // If user already has a profile, redirect to projects
  useEffect(() => {
    if (onboardingStatus?.isOnboarded) {
      navigate({ to: "/projects" });
    }
  }, [onboardingStatus, navigate]);

  const form = useForm<OnboardingFormData>({
    initialValues: {
      name: userFullName,
      email: userEmail,
    },
    validate: {
      name: (value) =>
        value.trim().length < 2 ? "Your name is required" : null,
      email: (value) =>
        /^\S+@\S+\.\S+$/.test(value) ? null : "Valid email is required",
    },
  });

  // Update form when Clerk user data loads
  useEffect(() => {
    if (userEmail && !form.values.email) {
      form.setFieldValue("email", userEmail);
    }
    if (userFullName && !form.values.name) {
      form.setFieldValue("name", userFullName);
    }
  }, [userEmail, userFullName]);

  const mutation = useMutation({
    mutationFn: async (data: OnboardingFormData) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();
      return fetchApi<OnboardingResponse>("/api/onboarding/profile", {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: data.name,
          email: data.email,
        }),
      });
    },
    onSuccess: () => {
      notifications.show({
        title: "Welcome to Kablan!",
        message: "Your profile has been created. Let's get started!",
        color: "green",
      });
      // Use window.location for reliable redirect
      window.location.href = "/projects";
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create profile",
        color: "red",
      });
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    mutation.mutate(values);
  });

  if (isLoadingStatus) {
    return (
      <Container size="xs" py="xl">
        <Paper p="xl" withBorder>
          <Stack gap="md">
            <Text ta="center">Loading...</Text>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="xs" py="xl">
      <Paper p="xl" withBorder>
        <Stack gap="md">
          <Title order={2} ta="center">
            Welcome to Kablan
          </Title>
          <Text size="sm" c="dimmed" ta="center">
            Let's set up your profile to get started
          </Text>

          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label="Your Name"
                placeholder="Enter your name"
                {...form.getInputProps("name")}
                required
              />

              <TextInput
                label="Email"
                placeholder="Enter your email"
                type="email"
                {...form.getInputProps("email")}
                required
                readOnly={!!userEmail}
                description={userEmail ? "From your account" : undefined}
              />

              <Button
                type="submit"
                fullWidth
                size="md"
                loading={mutation.isPending}
              >
                Get Started
              </Button>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Container>
  );
}
